import axios from "axios";
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { environment } from "../config/environment";
import { API_CONFIG, API_ENDPOINTS } from "../../shared/constants/api";
import { StorageService } from "../storage/storage-service";
import { STORAGE_KEYS } from "../../shared/constants/storage";
import type { ApiResponse } from "../../shared/types/api";

export interface HttpClient {
  get<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>>;
  post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>>;
  put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>>;
  delete<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>>;
  patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>>;
}

class AxiosHttpClient implements HttpClient {
  private client: AxiosInstance;
  private storageService: StorageService;

  constructor() {
    this.storageService = new StorageService();
    this.client = axios.create({
      baseURL: environment.apiBaseUrl,
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor - 토큰 자동 추가
    this.client.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        const token = this.storageService.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - 토큰 갱신 및 에러 처리
    this.client.interceptors.response.use(
      (response) => {
        // API 응답이 성공이지만 비즈니스 로직상 실패인 경우 처리
        if (
          response.data &&
          typeof response.data === "object" &&
          "errorCode" in response.data
        ) {
          const apiResponse = response.data as any;
          if (apiResponse.errorCode) {
            const error = new Error(
              apiResponse.errorMsg || "API 요청에 실패했습니다."
            );
            (error as any).code = apiResponse.errorCode;
            (error as any).errorCode = apiResponse.errorCode;
            // 409 에러 시 products 정보 유지
            if (apiResponse.data?.products) {
              (error as any).products = apiResponse.data.products;
            }
            // 중복 이메일(2002) 시 기존 계정의 로그인 수단 정보 유지
            if (apiResponse.data?.loginMethods) {
              (error as any).loginMethods = apiResponse.data.loginMethods;
            }
            return Promise.reject(error);
          }
        }
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // 401 에러 처리 - 토큰 갱신
        if ((error.response?.status === 401 || error.response?.status === 500) && !originalRequest._retry) {
          originalRequest._retry = true;

          // 로그인 페이지나 인증 관련 엔드포인트는 토큰 갱신 시도하지 않음
          const isAuthEndpoint = originalRequest.url?.includes("/api/auth/");
          if (isAuthEndpoint) {
            // reissue 요청이 실패하면 로그아웃 처리
            if (originalRequest.url?.includes("/api/auth/reissue")) {
              this.clearAuthData();
              window.location.href = "/login";
            }
            return Promise.reject(error);
          }

          try {
            const refreshToken = this.storageService.getItem(
              STORAGE_KEYS.REFRESH_TOKEN
            );

            if (!refreshToken) {
              // 리프레시 토큰이 없으면 로그아웃 처리
              this.clearAuthData();
              window.location.href = "/login";
              return Promise.reject(error);
            }

            const response = await this.reissueAccessToken(refreshToken);

            // 응답 구조 수정: response.data가 직접 TokenResponse일 수 있음
            const tokenData = response.data.data || response.data;
            const { accessToken, refreshToken: newRefreshToken } = tokenData;

            if (!accessToken) {
              console.error("[Auth] 재발급 응답에 accessToken이 없습니다:", tokenData);
              throw new Error("Invalid token response");
            }

            // 새로운 토큰들을 저장
            this.storageService.setItem(
              STORAGE_KEYS.ACCESS_TOKEN,
              accessToken,
            );

            if (newRefreshToken) {
              this.storageService.setItem(
                STORAGE_KEYS.REFRESH_TOKEN,
                newRefreshToken,
              );
            }

            // 원래 요청에 새로운 액세스 토큰 설정
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;

            return this.client(originalRequest);
          } catch (refreshError) {
            // 토큰 갱신 실패 시 로그아웃 처리
            this.clearAuthData();
            window.location.href = "/login";
            return Promise.reject(refreshError);
          }
        }

        // 에러 응답 형식 표준화
        if (error.response?.data) {
          const errorData = error.response.data as any;
          const customError = new Error(errorData.errorMsg || error.message);
          (customError as any).status = error.response.status;
          (customError as any).code = errorData.errorCode;
          (customError as any).errorCode = errorData.errorCode;
          // 409 에러 시 products 정보 유지
          if (errorData.data?.products) {
            (customError as any).products = errorData.data.products;
          }
          // 잉크 삭제 409 시 사용 중인 인쇄 구성 정보 유지
          if (errorData.data?.inkConfigs) {
            (customError as any).inkConfigs = errorData.data.inkConfigs;
          }
          // 중복 이메일(2002) 시 기존 계정의 로그인 수단 정보 유지
          if (errorData.data?.loginMethods) {
            (customError as any).loginMethods = errorData.data.loginMethods;
          }
          // 구조화된 에러 본문은 통째로 넘긴다.
          // (상품 삭제 재확인의 data.usages 처럼 products 외의 페이로드도 살려야 한다)
          if (errorData.data) {
            (customError as any).data = errorData.data;
          }
          return Promise.reject(customError);
        }

        return Promise.reject(error);
      }
    );
  }

  private async reissueAccessToken(
    refreshToken: string
  ): Promise<AxiosResponse> {
    const newClient = axios.create({
      baseURL: environment.apiBaseUrl,
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        "Content-Type": "application/json",
      },
    });
    const response = await newClient.post(
      API_ENDPOINTS.AUTH.REISSUE,
      {},
      {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      }
    );

    return response;
  }

  private clearAuthData(): void {
    this.storageService.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    this.storageService.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    this.storageService.removeItem(STORAGE_KEYS.USER_INFO);

    // Also remove from localStorage since Zustand persist stores there by default
    localStorage.removeItem(STORAGE_KEYS.USER_INFO);
  }

  async get<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.client.get<T>(url, config);
  }

  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.client.post<T>(url, data, config);
  }

  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.client.put<T>(url, data, config);
  }

  async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.client.delete<T>(url, config);
  }

  async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.client.patch<T>(url, data, config);
  }
}

export const httpClient = new AxiosHttpClient();
